import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ApiService } from '../../_core/_services/api.service';
import { NotificationService } from '../../_core/_services/notification.service';
import { CountryChangeRequestListItemDto } from '../../_core/_model/country-change';
import { ApiMeta } from '../../_core/_model/api-response';
import { Router } from '@angular/router';

@Component({
  selector: 'app-country-change-requests',
  standalone: false,
  templateUrl: './country-change-requests.html',
  styleUrl: './country-change-requests.css'
})
export class CountryChangeRequests implements OnInit {

  loading = false;
  requests: CountryChangeRequestListItemDto[] = [];
  meta!: ApiMeta;

  searchForm!: FormGroup;
  page = 1;

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private notify: NotificationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.searchForm = this.fb.group({
      status: ['Pending'],
      search: [''],
      currentCountryCode: [''],
      requestedCountryCode: [''],
      requestedByType: [''],
      riskLevel: [''],
      requiresManualReview: [''],
      hasOpenComplianceFlags: ['']
    });

    this.load();
  }

  load(page: number = 1) {
    this.loading = true;
    this.page = page;

    const raw = this.searchForm.value;

    const filters = {
      status: raw.status || undefined,
      search: raw.search || undefined,
      currentCountryCode: raw.currentCountryCode || undefined,
      requestedCountryCode: raw.requestedCountryCode || undefined,
      requestedByType: raw.requestedByType || undefined,
      riskLevel: raw.riskLevel || undefined,
      requiresManualReview: raw.requiresManualReview === '' ? undefined : raw.requiresManualReview === 'true',
      hasOpenComplianceFlags: raw.hasOpenComplianceFlags === '' ? undefined : raw.hasOpenComplianceFlags === 'true',
      page: this.page,
      size: 10
    };

    this.api.getCountryChangeRequests(filters).subscribe({
      next: (res) => {
        this.requests = res.data;
        this.meta = res.meta as ApiMeta;
      },
      error: (err) => {
        this.notify.showError(err?.error?.message || 'Failed to load', 'Error');
      },
      complete: () => this.loading = false
    });
  }

  submitSearch() {
    this.load(1);
  }

  resetFilters() {
    this.searchForm.patchValue({
      status: 'Pending',
      search: '',
      currentCountryCode: '',
      requestedCountryCode: '',
      requestedByType: '',
      riskLevel: '',
      requiresManualReview: '',
      hasOpenComplianceFlags: ''
    });

    this.load(1);
  }

  view(item: CountryChangeRequestListItemDto) {
    this.router.navigate(['/admin/country-change-requests', item.id]);
  }

  approve(item: CountryChangeRequestListItemDto) {
    if (!confirm('Approve this request?')) return;

    this.api.approveCountryChange(item.id, { notes: 'Approved' }).subscribe({
      next: () => {
        this.notify.showSuccess('Approved successfully', 'Success');
        this.load(this.page);
      },
      error: (err) => {
        this.notify.showError(err?.error?.message || 'Failed to approve', 'Error');
      }
    });
  }

  reject(item: CountryChangeRequestListItemDto) {
    const notes = prompt('Enter rejection reason');
    if (!notes) return;

    this.api.rejectCountryChange(item.id, { notes }).subscribe({
      next: () => {
        this.notify.showSuccess('Rejected successfully', 'Success');
        this.load(this.page);
      },
      error: (err) => {
        this.notify.showError(err?.error?.message || 'Failed to reject', 'Error');
      }
    });
  }

  getStatusBadgeClass(status?: string): string {
    switch (status) {
      case 'Approved':
      case 'Applied':
        return 'text-bg-success';
      case 'Rejected':
        return 'text-bg-danger';
      default:
        return 'text-bg-warning';
    }
  }

  getRiskBadgeClass(level?: string | null): string {
    switch (level) {
      case 'Critical':
        return 'text-bg-danger';
      case 'High':
        return 'text-bg-warning';
      case 'Medium':
        return 'text-bg-info';
      case 'Low':
        return 'text-bg-secondary';
      default:
        return 'text-bg-light';
    }
  }

  getDisplayName(item: CountryChangeRequestListItemDto): string {
    return item.fullName || item.userFullName || item.email || item.userEmail || item.userId;
  }

  getDisplayEmail(item: CountryChangeRequestListItemDto): string {
    return item.email || item.userEmail || '';
  }

  getRequestedDate(item: CountryChangeRequestListItemDto): string | undefined {
    return item.createdAt || item.requestedAt;
  }
}